import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './app-shell';
import { PlaceholderPage } from './placeholder-page';
import { LoginPage } from './auth/login-page';
import { ChangePasswordPage } from './auth/change-password-page';
import { RequireAuth } from './auth/require-auth';
import { SettingsPage } from './settings/settings-page';
import { UsersPage } from './users/users-page';
import { UserDetailPage } from './users/user-detail-page';
import { RolesPage } from './users/roles-page';
import { WarehouseSelectPage } from './warehouses/warehouse-select-page';
import { RequireWarehouse } from './warehouses/require-warehouse';
import { LocationTreePage } from './location/location-tree-page';
import { LayoutEditorPage } from './layout/layout-editor-page';
import { ProductListPage } from './product/product-list-page';
import { ProductCreatePage } from './product/product-create-page';
import { ProductDetailPage } from './product/product-detail-page';
import { ArchiveBatchesPage } from './product/archive-batches-page';
import { ArchiveBatchDetailPage } from './product/archive-batch-detail-page';
import { SupplierListPage } from './supplier/supplier-list-page';
import { DashboardPage } from './inventory/dashboard-page';
import { InventoryPage } from './inventory/inventory-page';
import { MovementsPage } from './inventory/movements-page';
import { SearchPage } from './inventory/search-page';
import { OrdersListPage } from './orders/orders-list-page';
import { OrderCreatePage } from './orders/order-create-page';
import { OrderDetailPage } from './orders/order-detail-page';
import { AlertCenterPage } from './alerts/alert-center-page';
import { StockCountsListPage } from './stockcount/stock-counts-list-page';
import { StockCountDetailPage } from './stockcount/stock-count-detail-page';
import { ImportJobsListPage } from './import-export/import-jobs-list-page';
import { ImportJobDetailPage } from './import-export/import-job-detail-page';
import { ExportCenterPage } from './import-export/export-center-page';
import { AuditLogPage } from './audit/audit-log-page';
import { BackupStatusPage } from './backup/backup-status-page';

/**
 * 路由骨架。每个占位页对应 UX 设计书第二部分的一个屏幕，
 * 在各自的里程碑中被真实实现替换。M1 起补上认证 + 会话 + RBAC 屏幕。
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      { path: 'change-password', element: <ChangePasswordPage forced /> },
      { path: 'warehouses', element: <WarehouseSelectPage /> },
      {
        path: '/',
        element: <RequireWarehouse />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              // M5 首版 + M8 补全（attention 卡片条、库存价值、预留数）
              { path: 'dashboard', element: <DashboardPage /> },
              { path: 'inventory', element: <InventoryPage /> },
              { path: 'search', element: <SearchPage /> },
              // M3a：/layout 是画布编辑器（UX 设计书 S4）；M2 的位置树页保留在
              // /locations，两者互补——树擅长搜索与整棵结构，画布擅长空间布置。
              { path: 'layout', element: <LayoutEditorPage /> },
              { path: 'locations', element: <LocationTreePage /> },
              { path: 'movements', element: <MovementsPage /> },
              // M7：S12/F6 —— 预留/订单
              { path: 'orders', element: <OrdersListPage /> },
              { path: 'orders/new', element: <OrderCreatePage /> },
              { path: 'orders/:id', element: <OrderDetailPage /> },
              // M8：S11 —— 预警中心
              { path: 'alerts', element: <AlertCenterPage /> },
              // M9：S13 —— 库存盘点
              { path: 'stock-counts', element: <StockCountsListPage /> },
              { path: 'stock-counts/:id', element: <StockCountDetailPage /> },
              // M10：S17/S14 —— 导入中心 / 导出中心
              { path: 'import-jobs', element: <ImportJobsListPage /> },
              { path: 'import-jobs/:id', element: <ImportJobDetailPage /> },
              { path: 'exports', element: <ExportCenterPage /> },
              // M11：审计日志查询 + override 授权人报表
              { path: 'audit-log', element: <AuditLogPage /> },
              // M11：备份状态只读展示
              { path: 'backup-status', element: <BackupStatusPage /> },
              { path: 'products', element: <ProductListPage /> },
              { path: 'products/new', element: <ProductCreatePage /> },
              // 0023：批量归档批次页——静态路径，React Router v6 按特异度匹配，
              // 不需要排在 products/:id 之前，但仍就近放在一起方便阅读。
              { path: 'products/archive-batches', element: <ArchiveBatchesPage /> },
              { path: 'products/archive-batches/:id', element: <ArchiveBatchDetailPage /> },
              { path: 'products/:id', element: <ProductDetailPage /> },
              { path: 'suppliers', element: <SupplierListPage /> },
              { path: 'settings', element: <SettingsPage /> },
              { path: 'settings/users', element: <UsersPage /> },
              { path: 'settings/users/:userId', element: <UserDetailPage /> },
              { path: 'settings/roles', element: <RolesPage /> },
              { path: '*', element: <PlaceholderPage titleKey="shell.notFound" milestone="—" /> },
            ],
          },
        ],
      },
    ],
  },
]);
